import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../auth/services/auth.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { MatInputComponent } from '../mat-input/mat-input.component';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';
import { RoleAccessService, Role } from '../../auth/services/role-access.service';
import { FEATURE_TO_ROUTE_MAP } from '../../auth/feature-constants';
import { FeatureConfigService } from '../../auth/services/feature-config.service';
import { MenuItem, Feature } from '../../auth/models/feature.model';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    CommonModule,
    MatInputComponent
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {
  user: { username?: string; id?: string; role?: Role } = {};
  expandedPanel: string | null = null;
  searchText = '';
  currentRoute: string = '';
  private destroy$ = new Subject<void>();

  // Menu items loaded from feature configuration
  menuItems$: Observable<MenuItem[]>;

  // Computed properties for better performance
  get accessibleMenuItems(): MenuItem[] {
    // This will be handled by the template using async pipe
    return [];
  }

  constructor(
    private authService: AuthService,
    private router: Router,
    private roleAccessService: RoleAccessService,
    private featureConfigService: FeatureConfigService
  ) {
    this.initializeUser();
    this.setupRouteListener();
    this.menuItems$ = this.featureConfigService.getMenuItems();
  }

  ngOnInit(): void {
    // Component initialization logic
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeUser(): void {
    const decoded = this.authService.decodeToken();
    this.user = {
      username: decoded?.userName || decoded?.name || 'User',
      id: decoded?.userId || decoded?.id || '0',
      role: this.roleAccessService.getUserRole()
    };
  }

  private setupRouteListener(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.url;
      });
  }

  logout(): void {
    this.authService.clearToken();
    window.location.href = '/login';
  }

  searchTextChange(value: string): void {
    this.searchText = value;
    // Implement search functionality if needed
  }

  togglePanel(panelId: string): void {
    this.expandedPanel = this.expandedPanel === panelId ? null : panelId;
  }

  isPanelExpanded(panelId: string): boolean {
    return this.expandedPanel === panelId;
  }

  isRouteActive(route: string): boolean {
    return this.currentRoute === route;
  }

  isTripManagementActive(route: string): boolean {
    if (route === FEATURE_TO_ROUTE_MAP[Feature.TRIP]) {
      return this.currentRoute === FEATURE_TO_ROUTE_MAP[Feature.TRIP] && !this.currentRoute.includes(FEATURE_TO_ROUTE_MAP[Feature.SCHEDULE_TRIP]);
    } else if (route === FEATURE_TO_ROUTE_MAP[Feature.SCHEDULE_TRIP]) {
      return this.currentRoute === FEATURE_TO_ROUTE_MAP[Feature.SCHEDULE_TRIP];
    }
    return false;
  }

  // Optimized access control methods
  canAccessMenuItem(item: MenuItem): boolean {
    // Special handling for Trip Management with multiple features
    if (item.id === 'tripManagement') {
      return this.hasMultipleTripAccess() || this.canAccessTripOnly() || this.canAccessScheduleTripOnly();
    }

    // Special handling for Transit Management with multiple features
    if (item.id === 'transitManagement') {
      return this.hasMultipleTransitAccess() || this.canAccessTransitOnly() || this.canAccessCityRestrictionOnly();
    }

    // Special handling for User Management
    if (item.id === 'userManagement') {
      return this.canAccessUserManagement();
    }

    // For items with children, check if any child is accessible
    if (item.type === 'group' && item.children) {
      return item.children.some((child: MenuItem) => this.roleAccessService.canAccess(child.feature));
    }

    // For regular items, check direct feature access
    return this.roleAccessService.canAccess(item.feature);
  }

  // Trip Management access methods
  hasMultipleTripAccess(): boolean {
    return this.roleAccessService.canAccess(Feature.TRIP) && this.roleAccessService.canAccess(Feature.SCHEDULE_TRIP);
  }

  canAccessTripOnly(): boolean {
    return this.roleAccessService.canAccess(Feature.TRIP) && !this.roleAccessService.canAccess(Feature.SCHEDULE_TRIP);
  }

  canAccessScheduleTripOnly(): boolean {
    return !this.roleAccessService.canAccess(Feature.TRIP) && this.roleAccessService.canAccess(Feature.SCHEDULE_TRIP);
  }

  // Transit Management access methods
  hasMultipleTransitAccess(): boolean {
    return this.roleAccessService.canAccess(Feature.TRANSIT_MANAGEMENT) && this.roleAccessService.canAccess(Feature.CITY_RESTRICTION);
  }

  canAccessTransitOnly(): boolean {
    return this.roleAccessService.canAccess(Feature.TRANSIT_MANAGEMENT) && !this.roleAccessService.canAccess(Feature.CITY_RESTRICTION);
  }

  canAccessCityRestrictionOnly(): boolean {
    return !this.roleAccessService.canAccess(Feature.TRANSIT_MANAGEMENT) && this.roleAccessService.canAccess(Feature.CITY_RESTRICTION);
  }

  // User Management access method
  canAccessUserManagement(): boolean {
    return this.roleAccessService.canAccess(Feature.USER_MANAGEMENT);
  }

  // Helper methods for template
  shouldShowAsExpansion(item: MenuItem): boolean {
    if (item.id === 'tripManagement') {
      return this.hasMultipleTripAccess();
    }
    if (item.id === 'transitManagement') {
      return this.hasMultipleTransitAccess();
    }
    return item.type === 'group';
  }

  shouldShowAsSingleItem(item: MenuItem): boolean {
    if (item.id === 'tripManagement') {
      return this.canAccessTripOnly() || this.canAccessScheduleTripOnly();
    }
    if (item.id === 'transitManagement') {
      return this.canAccessTransitOnly() || this.canAccessCityRestrictionOnly();
    }
    return item.type === 'single';
  }

  getSingleItemRoute(item: MenuItem): string {
    if (item.id === 'tripManagement') {
      if (this.canAccessTripOnly()) {
        return FEATURE_TO_ROUTE_MAP[Feature.TRIP];
      } else if (this.canAccessScheduleTripOnly()) {
        return FEATURE_TO_ROUTE_MAP[Feature.SCHEDULE_TRIP];
      }
    }
    if (item.id === 'transitManagement') {
      if (this.canAccessTransitOnly()) {
        return FEATURE_TO_ROUTE_MAP[Feature.TRANSIT_MANAGEMENT];
      } else if (this.canAccessCityRestrictionOnly()) {
        return FEATURE_TO_ROUTE_MAP[Feature.CITY_RESTRICTION];
      }
    }
    return item.route || '';
  }

  getSingleItemLabel(item: MenuItem): string {
    if (item.id === 'tripManagement') {
      if (this.canAccessTripOnly()) {
        return 'Trips';
      } else if (this.canAccessScheduleTripOnly()) {
        return 'Schedule Trips';
      }
    }
    if (item.id === 'transitManagement') {
      if (this.canAccessTransitOnly()) {
        return 'Transit';
      } else if (this.canAccessCityRestrictionOnly()) {
        return 'City Restriction';
      }
    }
    return item.label;
  }
}
